import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { ProfileLayout } from "../components/ProfileLayout"
import { Calendar, FileText, CreditCard, Clock, MapPin, Phone, Download } from "lucide-react"
import { getCookie } from "../utils/cookies"
import { Button } from "../components/ui/button.tsx"
import api from "../utils/api"

function toTitleCase(str) {
  if (!str) return '';
  return str.trim().toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

export default function PortalDashboard() {
  const navigate = useNavigate()
  const [counts, setCounts] = useState({ lab: 0, prescription: 0 })
  const [user, setUser] = useState(null)
  const [recentReports, setRecentReports] = useState([])
  const [reportsLoading, setReportsLoading] = useState(true)
  const [recentAppointments, setRecentAppointments] = useState([])
  const [appointmentsLoading, setAppointmentsLoading] = useState(true)

  useEffect(() => {
    const userStr = localStorage.getItem("user") || getCookie("user");
    
    if (!userStr || userStr === "undefined") {
      navigate("/");
      return;
    }

    if (userStr) {
      const userData = JSON.parse(userStr)
      setUser(userData)
      
      let mobile = userData?.mobile || userData?.phone || ""
      if (mobile) {
        if (!mobile.startsWith("91")) {
          mobile = `91${mobile}`
        }
        
        api.get(`/apis/web/prescriptionandlabcount?mobile=${mobile}`)
          .then(res => {
            const data = res.data
            if (data.status === 0 && data.result?.[0]) {
              setCounts({
                lab: data.result[0].LAB_REPORT_COUNT || 0,
                prescription: data.result[0].PRESCRIPTION_COUNT || 0
              })
            }
          })
          .catch(err => console.error("Error fetching counts:", err))

        api.get(`/apis/web/toplabreports?mobile=${mobile}`)
          .then(res => {
            if (res.data?.result && Array.isArray(res.data.result)) {
              const mapped = res.data.result.slice(0, 5).map((item, index) => {
                const trnDate = item.TRNDATE ? new Date(item.TRNDATE) : null
                const resDate = (item.RESULT_DATE || item.APPROVED_DATE) ? new Date(item.RESULT_DATE || item.APPROVED_DATE) : null
                return {
                  id: item.TEST_ID || index + 1,
                  patientName: toTitleCase(item.PATIENT_NAME),
                  mrNumber: item.MRNO || "N/A",
                  orderDate: trnDate ? trnDate.toLocaleDateString('en-GB') : "N/A",
                  resultDate: resDate ? resDate.toLocaleDateString('en-GB') : "Pending",
                  downloadUrl: item.WHATSAPP_URL || ""
                }
              })
              setRecentReports(mapped)
            }
          })
          .catch(err => console.error("Error fetching reports:", err))
          .finally(() => setReportsLoading(false))

        api.get(`/apis/web/todayappointments?mobile=${mobile}`)
          .then(res => {
            if (res.data?.result && Array.isArray(res.data.result)) {
              const mapped = res.data.result.slice(0, 4).map((item, index) => {
                let formattedTime = ""
                const rawTime = item.APPOINTMENTTIME || ""
                if (rawTime && rawTime !== "00:00") {
                  const [h, m] = rawTime.split(":")
                  const hour = parseInt(h)
                  if (!isNaN(hour)) {
                    const ampm = hour >= 12 ? "PM" : "AM"
                    formattedTime = `${hour % 12 || 12}:${m} ${ampm}`
                  }
                }
                return {
                  id: item.APPOINTMENTNO || index + 1,
                  patientName: toTitleCase(item.PATIENT_NAME),
                  mrNo: item.PatientMr_No || "",
                  doctorName: toTitleCase(item.DOCTNAME),
                  department: toTitleCase(item.DEPTNAME),
                  consType: item.CONSTYPE === "006" ? "Video Consultation" : "General Consultation",
                  date: item.APPOINTMENTDATE || "",
                  time: formattedTime,
                  location: item.HospitalName || "",
                  bill: item.VISITAMOUNT ? `₹${item.VISITAMOUNT}` : "",
                  status: item.CONSULTED === "Y" ? "completed" : (item.VISITSTATUS || "").toLowerCase()
                }
              })
              setRecentAppointments(mapped)
            }
          })
          .catch(err => console.error("Error fetching appointments:", err))
          .finally(() => setAppointmentsLoading(false))
      }
    }
  }, [])

  const handleLogout = () => {
    localStorage.removeItem("user")
    navigate("/")
  }

  const quickActions = [
    { name: "My Appointments", icon: Clock, href: "/my-bookings", color: "bg-green-500" },
    { name: "Lab Reports", icon: FileText, href: "/my-reports", color: "bg-purple-500" },
    { name: "Prescriptions", icon: FileText, href: "/prescriptions", color: "bg-orange-500" },
    { name: "Bills & Payments", icon: CreditCard, href: "/bills-receipts", color: "bg-red-500" },
  ]

  const getStatusColor = (status) => {
    switch(status) {
      case "paid": return "bg-green-50 text-green-700 border-green-200"
      case "completed": return "bg-blue-50 text-blue-700 border-blue-200"
      case "cancelled": return "bg-red-50 text-red-700 border-red-200"
      case "pending": return "bg-yellow-50 text-yellow-700 border-yellow-200"
      default: return "bg-gray-50 text-gray-700 border-gray-200"
    }
  }

  return (
    <ProfileLayout onLogout={handleLogout}>
      <div className="bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white shadow-sm border-b border-gray-200 p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Portal Dashboard</h1>
                <p className="text-gray-600 mt-1">Welcome back, {user?.name || "User"}</p>
              </div>
              <Button className="gap-2 w-full sm:w-auto" onClick={() => navigate("/book-now")}>
                <Calendar className="w-4 h-4" />
                Book an Appointment
              </Button>
            </div>
          </div>

          <div className="px-4 md:px-8">

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {quickActions.map((action) => (
                <button
                  key={action.name}
                  onClick={() => navigate(action.href)}
                  className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors border border-gray-200"
                >
                  <div className={`w-12 h-12 ${action.color} rounded-full flex items-center justify-center mx-auto mb-3`}>
                    <action.icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-sm font-medium text-gray-900 text-center">{action.name}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Recent Appointments */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Today Appointments</h2>
              <button onClick={() => navigate("/my-bookings")} className="text-blue-600 text-sm font-medium hover:underline">
                View All
              </button>
            </div>
            {appointmentsLoading ? (
              <div className="py-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                <p className="mt-3 text-gray-500 text-sm">Loading appointments...</p>
              </div>
            ) : recentAppointments.length === 0 ? (
              <div className="p-8 text-center">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No recent appointments</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {recentAppointments.map((booking) => (
                  <div key={booking.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">{booking.patientName}</h3>
                        <p className="text-sm text-gray-600">{booking.mrNo}</p>
                        <p className="text-sm text-gray-600">{booking.doctorName} | {booking.department}</p>
                        <p className="text-xs font-medium mt-1">
                          <span className={booking.consType === "Video Consultation" ? "text-blue-600" : "text-green-600"}>{booking.consType}</span>
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded-md text-xs font-medium border ${getStatusColor(booking.status)}`}>
                        {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{booking.date}</span>
                        {booking.time && <><Clock className="w-4 h-4 ml-3" /><span>{booking.time}</span></>}
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{booking.location}</span>
                        {booking.bill && <span className="ml-auto font-medium text-gray-900">{booking.bill}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Reports & Results */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-8">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Recent Reports & Results</h2>
              <button onClick={() => navigate("/my-reports")} className="text-blue-600 text-sm font-medium hover:underline">
                View All
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-medium text-gray-700">Patient Name</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-medium text-gray-700">MR Number</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-medium text-gray-700">Order Date</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-medium text-gray-700">Result Date</th>
                    <th className="px-4 md:px-6 py-3 text-center text-xs md:text-sm font-medium text-gray-700">Download</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {reportsLoading ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                          <p className="text-gray-500">Loading reports...</p>
                        </div>
                      </td>
                    </tr>
                  ) : recentReports.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 md:px-6 py-12 text-center text-gray-500 text-sm">No reports available</td>
                    </tr>
                  ) : (
                    recentReports.map((report) => (
                      <tr key={report.id} className="hover:bg-gray-50">
                        <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-900 font-medium">{report.patientName}</td>
                        <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-600 font-mono">{report.mrNumber}</td>
                        <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-600">{report.orderDate}</td>
                        <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-600">{report.resultDate}</td>
                        <td className="px-4 md:px-6 py-4 text-center">
                          {report.downloadUrl ? (
                            <a href={report.downloadUrl} target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-800 inline-block">
                              <Download className="w-5 h-5" />
                            </a>
                          ) : (
                            <span className="text-gray-300"><Download className="w-5 h-5" /></span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Health Summary */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{counts.lab}</p>
                  <p className="text-sm text-gray-600">Lab Reports</p>
                </div>
              </div>
              <button onClick={() => navigate("/my-reports")} className="text-blue-600 text-sm font-medium hover:underline">
                View Reports
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{counts.prescription}</p>
                  <p className="text-sm text-gray-600">Prescriptions</p>
                </div>
              </div>
              <button onClick={() => navigate("/prescriptions")} className="text-purple-600 text-sm font-medium hover:underline">
                View Prescriptions
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Phone className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">24/7</p>
                  <p className="text-sm text-gray-600">Emergency</p>
                </div>
              </div>
              <a href="tel:+919966060000" className="text-red-600 text-sm font-medium hover:underline">
                +91 99660 60000
              </a>
            </div>
          </div>
          </div>
        </div>
      </div>
    </ProfileLayout>
  )
}
